/**
 * Avondale flu declination contract shared by the portal client and server.
 *
 * The 2026-2027 form mirrors the Link declination: name, DOB, reason checkboxes, and an
 * "Other" explanation. Templates are blob-backed PdfTemplates named `templateName` with
 * `templateFormName`, one row per language in `languages`.
 */
export const AVONDALE_FLU_DECLINATION = {
  title: 'Flu Declination',
  templateName: 'Avondale Flu Declination',
  templateFormName: 'standalone-document',
  languages: ['English', 'Spanish'],
  defaultLanguage: 'English',
} as const;

export type AvondaleFluLanguage = typeof AVONDALE_FLU_DECLINATION.languages[number];

export const AVONDALE_FLU_REASONS = [
  { value: 'FearBelieve', label: 'FEAR - I believe I will get influenza if I get the vaccine' },
  { value: 'FearNeedles', label: 'FEAR - I do not like needles' },
  { value: 'Religion', label: 'RELIGION - My philosophical and/or religious beliefs prohibit vaccination' },
  { value: 'Allergic', label: 'ALLERGIC - I have an allergy or medical contraindication to receiving the vaccine' },
  { value: 'Other', label: 'Other - Please explain' },
] as const;

export type AvondaleFluReason = typeof AVONDALE_FLU_REASONS[number]['value'];

/** PDF field names on the Avondale flu declination template. `Id` remains for the pre-2026 template. */
export const AVONDALE_FLU_PDF_FIELDS = {
  name: '{!Name}',
  employeeId: '{!Id}',
  dateOfBirth: '{!DOB}',
  date: '{!Date}',
  signature: '{!Signature}',
  otherReason: '{!OtherReason}',
  reason: (reason: AvondaleFluReason) => `{!Reason${reason}}`,
} as const;

export interface AvondaleFluDeclinationRequest {
  name: string;
  employeeId?: string;
  /** YYYY-MM-DD */
  dateOfBirth: string;
  reasons: AvondaleFluReason[];
  otherReason?: string;
  language: AvondaleFluLanguage;
  signature: string;
}

export function normalizeAvondaleFluLanguage(value: unknown): AvondaleFluLanguage {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'spanish' || normalized === 'es' ? 'Spanish' : 'English';
}

export function normalizeAvondaleFluReasons(value: unknown): AvondaleFluReason[] {
  const raw: unknown[] = Array.isArray(value) ? value : [];
  return AVONDALE_FLU_REASONS
    .map((reason) => reason.value)
    .filter((reason) => raw.includes(reason));
}
